import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import {
  FileText, Plus, Copy, Check, Trash2, Eye, Send,
  TrendingUp, Moon, RefreshCw, Settings,
  Edit2, ToggleLeft, ToggleRight, Tag, X, Sparkles, Search, Download, Upload, Sun, BarChart3, Star, Heart, Zap, Award, Save, Calendar, Image, DollarSign, Clock, Play, Pause, LayoutDashboard, ChevronLeft, ChevronRight, ExternalLink, Globe, ArrowUp, ArrowDown, Minus, PieChart, User, Lock, LogOut, Users, UserPlus, Shield, Key, ToggleRight as Toggle, Eye as EyeIcon, EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4005/api';

// ============================================
// API 함수
// ============================================

const api = {
  getPosts: (params) => axios.get(`${API_URL}/posts`, { params }),
  getPost: (id) => axios.get(`${API_URL}/posts/${id}`),
  generatePost: (data) => axios.post(`${API_URL}/posts/generate`, data),
  generateBatch: (data) => axios.post(`${API_URL}/posts/generate-batch`, data),
  updateStatus: (id, status) => axios.patch(`${API_URL}/posts/${id}/status`, { status }),
  deletePost: (id) => axios.delete(`${API_URL}/posts/${id}`),
  getStats: () => axios.get(`${API_URL}/stats`),
  getKeywords: (site) => axios.get(`${API_URL}/keywords`, { params: { site } }),
  addKeyword: (data) => axios.post(`${API_URL}/keywords`, data),
  updateKeyword: (id, data) => axios.put(`${API_URL}/keywords/${id}`, data),
  deleteKeyword: (id) => axios.delete(`${API_URL}/keywords/${id}`),
  toggleKeyword: (id) => axios.patch(`${API_URL}/keywords/${id}/toggle`),
  getCategories: (site) => axios.get(`${API_URL}/categories`, { params: { site } }),
  addCategory: (data) => axios.post(`${API_URL}/categories`, data),
  deleteCategory: (id) => axios.delete(`${API_URL}/categories/${id}`),
  getSites: () => axios.get(`${API_URL}/sites`),
  addSite: (data) => axios.post(`${API_URL}/sites`, data),
  updateSite: (id, data) => axios.put(`${API_URL}/sites/${id}`, data),
  deleteSite: (id) => axios.delete(`${API_URL}/sites/${id}`),
  getModels: () => axios.get(`${API_URL}/models`),
  regeneratePost: (id, data) => axios.post(`${API_URL}/posts/${id}/regenerate`, data),
  updatePost: (id, data) => axios.put(`${API_URL}/posts/${id}`, data),
  exportPosts: (params) => axios.get(`${API_URL}/posts/export`, { params, responseType: 'blob' }),
  checkDuplicate: (data) => axios.post(`${API_URL}/posts/check-duplicate`, data),
  importKeywords: (formData) => axios.post(`${API_URL}/keywords/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getKeywordUsage: (site) => axios.get(`${API_URL}/keywords/usage`, { params: { site } }),
  toggleFavorite: (id) => axios.patch(`${API_URL}/posts/${id}/favorite`),
  getFavorites: () => axios.get(`${API_URL}/posts/favorites`),
  getUnusedKeywords: (site, limit) => axios.get(`${API_URL}/keywords/unused`, { params: { site, limit } }),
  getSeoScore: (id) => axios.get(`${API_URL}/posts/${id}/seo`),
  getTemplates: () => axios.get(`${API_URL}/templates`),
  saveTemplate: (data) => axios.post(`${API_URL}/templates`, data),
  deleteTemplate: (id) => axios.delete(`${API_URL}/templates/${id}`),
  // 예약 발행
  schedulePost: (id, scheduled_at) => axios.post(`${API_URL}/posts/${id}/schedule`, { scheduled_at }),
  cancelSchedule: (id) => axios.delete(`${API_URL}/posts/${id}/schedule`),
  getScheduledPosts: () => axios.get(`${API_URL}/posts/scheduled`),
  // A/B 테스트
  createAbTest: (data) => axios.post(`${API_URL}/posts/ab-test`, data),
  // 이미지 생성
  generateImage: (prompt, size) => axios.post(`${API_URL}/images/generate`, { prompt, size }),
  addImageToPost: (id, imageUrl) => axios.post(`${API_URL}/posts/${id}/image`, { imageUrl }),
  // 비용 추적
  getCosts: (period) => axios.get(`${API_URL}/costs`, { params: { period } }),
  // 키워드 추천
  recommendKeywords: (data) => axios.post(`${API_URL}/keywords/recommend`, data),
  // 대시보드
  getDashboard: () => axios.get(`${API_URL}/dashboard`),
  // 스케줄러
  getSchedulerJobs: () => axios.get(`${API_URL}/scheduler/jobs`),
  createSchedulerJob: (data) => axios.post(`${API_URL}/scheduler/jobs`, data),
  toggleSchedulerJob: (id) => axios.patch(`${API_URL}/scheduler/jobs/${id}/toggle`),
  deleteSchedulerJob: (id) => axios.delete(`${API_URL}/scheduler/jobs/${id}`),
  runSchedulerJob: (id) => axios.post(`${API_URL}/scheduler/jobs/${id}/run`),
  // 콘텐츠 캘린더
  getCalendar: (year, month) => axios.get(`${API_URL}/calendar`, { params: { year, month } }),
  // 키워드 성과 분석
  getKeywordAnalytics: () => axios.get(`${API_URL}/analytics/keywords`),
  // 트렌드 키워드
  getTrends: (site) => axios.get(`${API_URL}/trends`, { params: { site } }),
  // AI 썸네일
  generateThumbnail: (id, style) => axios.post(`${API_URL}/posts/${id}/thumbnail`, { style }),
  // 외부 사이트
  getExternalSites: () => axios.get(`${API_URL}/external-sites`),
  addExternalSite: (data) => axios.post(`${API_URL}/external-sites`, data),
  deleteExternalSite: (id) => axios.delete(`${API_URL}/external-sites/${id}`),
  publishExternal: (id, siteId) => axios.post(`${API_URL}/posts/${id}/publish-external`, { siteId }),
  getPublishLogs: (id) => axios.get(`${API_URL}/posts/${id}/publish-logs`),
  // 인증
  login: (data) => axios.post(`${API_URL}/auth/login`, data),
  // 사용자 관리
  getUsers: () => axios.get(`${API_URL}/users`),
  addUser: (data) => axios.post(`${API_URL}/users`, data),
  updateUser: (id, data) => axios.put(`${API_URL}/users/${id}`, data),
  deleteUser: (id) => axios.delete(`${API_URL}/users/${id}`),
  // 설정
  getSettings: () => axios.get(`${API_URL}/settings`),
  updateSettings: (settings) => axios.put(`${API_URL}/settings`, { settings }),
  updateSetting: (key, data) => axios.put(`${API_URL}/settings/${key}`, data),
};

// ============================================
// 통계 카드 컴포넌트
// ============================================

function StatsCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-amber-50 text-amber-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-violet-50 text-violet-600',
  };

  return (
    <div className="card p-5 flex items-center gap-4">
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-3xl font-bold text-slate-800">{value}</p>
        <p className="text-sm text-slate-500">{title}</p>
      </div>
    </div>
  );
}

// ============================================
// 글 생성 모달
// ============================================

// 글 스타일 옵션
const WRITING_STYLES = [
  { id: 'info', name: '정보 전달형', icon: '📚', desc: '객관적인 정보 중심' },
  { id: 'review', name: '후기/경험형', icon: '💬', desc: '개인 경험 기반 후기' },
  { id: 'compare', name: '비교 분석형', icon: '⚖️', desc: '장단점 비교 분석' },
  { id: 'qna', name: 'Q&A형', icon: '❓', desc: '질문과 답변 형식' },
  { id: 'story', name: '스토리형', icon: '📖', desc: '이야기 형식으로 전달' },
];

// 글 길이 옵션
const LENGTH_OPTIONS = [
  { id: 'short', name: '짧은글', icon: '📝', desc: '500자 내외' },
  { id: 'medium', name: '보통', icon: '📄', desc: '1000~1500자' },
  { id: 'long', name: '긴글', icon: '📚', desc: '2000자 이상' },
];

function GenerateModal({ isOpen, onClose, onGenerate }) {
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState('');
  const [category, setCategory] = useState('');
  const [style, setStyle] = useState('info');
  const [length, setLength] = useState('medium');
  const [model, setModel] = useState('gpt-4o-mini');
  const [models, setModels] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [keywords, setKeywords] = useState({ grouped: {} });
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // 사이트, AI 모델 목록 로드
  useEffect(() => {
    if (isOpen) {
      Promise.all([
        api.getSites(),
        api.getModels()
      ]).then(([sitesRes, modelsRes]) => {
        const siteList = sitesRes.data.sites || [];
        setSites(siteList);
        if (siteList.length > 0 && !site) {
          setSite(siteList[0].site_key);
        }
        setModels(modelsRes.data.models || []);
      });
    }
  }, [isOpen]);

  // 선택된 사이트의 카테고리/키워드 로드
  useEffect(() => {
    if (isOpen && site) {
      Promise.all([
        api.getCategories(site),
        api.getKeywords(site)
      ]).then(([catRes, kwRes]) => {
        setCategories(catRes.data.categories || []);
        setKeywords(kwRes.data);
        setSelectedKeywords([]);
      });
    }
  }, [isOpen, site]);

  // 카테고리 변경 시 키워드 선택 초기화
  useEffect(() => {
    setSelectedKeywords([]);
  }, [category]);

  // 현재 표시할 키워드 목록
  const allKeywords = category
    ? keywords.grouped?.[category] || []
    : keywords.keywords || [];

  // 미사용 키워드 우선 정렬
  const availableKeywords = [...allKeywords].sort((a, b) => {
    if (a.post_count === 0 && b.post_count > 0) return -1;
    if (a.post_count > 0 && b.post_count === 0) return 1;
    return 0;
  });

  const toggleKeyword = (kw) => {
    setSelectedKeywords(prev =>
      prev.find(k => k.id === kw.id)
        ? prev.filter(k => k.id !== kw.id)
        : [...prev, kw]
    );
  };

  const selectAll = () => {
    setSelectedKeywords(availableKeywords);
  };

  const deselectAll = () => {
    setSelectedKeywords([]);
  };

  const handleGenerate = async () => {
    if (selectedKeywords.length === 0) {
      toast.error('키워드를 선택하세요');
      return;
    }
    setLoading(true);
    try {
      // 선택된 키워드 ID 목록, 스타일, AI 모델 전송
      const keywordIds = selectedKeywords.map(k => k.id);
      const res = await api.generateBatch({ site, keywordIds, style, length, model });
      const successCount = res.data.results.filter(r => r.success).length;
      const selectedModel = models.find(m => m.key === model);
      toast.success(`${successCount}개 글 생성 완료! (${selectedModel?.name || model})`);
      onGenerate();
      onClose();
    } catch (error) {
      toast.error('생성 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="text-violet-500" size={24} />
            글 자동 생성
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* 사이트 선택 */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">1. 사이트 선택</label>
            <div className={`grid gap-3 ${sites.length <= 2 ? 'grid-cols-2' : sites.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {sites.map((s, index) => {
                const colors = [
                  { border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' },
                  { border: 'border-pink-500', bg: 'bg-pink-50', text: 'text-pink-700' },
                  { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
                  { border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
                  { border: 'border-violet-500', bg: 'bg-violet-50', text: 'text-violet-700' },
                  { border: 'border-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-700' },
                ];
                const color = colors[index % colors.length];
                return (
                  <button
                    key={s.id}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      site === s.site_key
                        ? `${color.border} ${color.bg} ${color.text}`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => { setSite(s.site_key); setCategory(''); }}
                  >
                    <span className="text-2xl">{s.icon}</span>
                    <span className="font-medium text-sm">{s.site_name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 카테고리 선택 */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">2. 카테고리 선택</label>
            <select
              className="select w-full"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <option value="">전체</option>
              {categories.map(cat => {
                const keywordCount = keywords.grouped?.[cat.category_key]?.length || 0;
                return (
                  <option key={cat.id} value={cat.category_key}>
                    {cat.category_name} ({keywordCount}개)
                  </option>
                );
              })}
            </select>
          </div>

          {/* 키워드 선택 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-slate-700">
                3. 키워드 선택
                <span className="ml-2 text-violet-600">({selectedKeywords.length}개 선택됨)</span>
              </label>
              <div className="flex gap-2">
                <button
                  className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  onClick={() => {
                    const unused = availableKeywords.filter(k => k.post_count === 0 || k.post_count === undefined);
                    setSelectedKeywords(unused);
                  }}
                >
                  미사용만
                </button>
                <span className="text-slate-300">|</span>
                <button
                  className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                  onClick={selectAll}
                >
                  전체선택
                </button>
                <span className="text-slate-300">|</span>
                <button
                  className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                  onClick={deselectAll}
                >
                  선택해제
                </button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-3 max-h-48 overflow-y-auto bg-slate-50">
              {availableKeywords.length === 0 ? (
                <p className="text-center text-slate-400 py-4 text-sm">키워드가 없습니다</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {availableKeywords.map(kw => {
                    const isSelected = selectedKeywords.find(k => k.id === kw.id);
                    const isUnused = kw.post_count === 0 || kw.post_count === undefined;
                    return (
                      <button
                        key={kw.id}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all relative ${
                          isSelected
                            ? 'bg-violet-600 text-white shadow-md'
                            : isUnused
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:border-emerald-400'
                              : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300'
                        }`}
                        onClick={() => toggleKeyword(kw)}
                      >
                        {isUnused && <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full"></span>}
                        {kw.keyword}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* 글 스타일 선택 */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">4. 글 스타일 선택</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {WRITING_STYLES.map(s => (
                <button
                  key={s.id}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    style === s.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setStyle(s.id)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{s.icon}</span>
                    <span className={`font-medium text-sm ${style === s.id ? 'text-violet-700' : 'text-slate-700'}`}>
                      {s.name}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* 글 길이 선택 */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">5. 글 길이 선택</label>
            <div className="grid grid-cols-3 gap-2">
              {LENGTH_OPTIONS.map(l => (
                <button
                  key={l.id}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    length === l.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setLength(l.id)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{l.icon}</span>
                    <span className={`font-medium text-sm ${length === l.id ? 'text-violet-700' : 'text-slate-700'}`}>
                      {l.name}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{l.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* AI 모델 선택 */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">6. AI 모델 선택</label>

            {/* 티어별 그룹 */}
            {['budget', 'standard', 'premium'].map(tier => {
              const tierModels = models.filter(m => m.tier === tier);
              if (tierModels.length === 0) return null;
              const tierLabels = { budget: '💰 저렴한 모델', standard: '⚡ 중급 모델', premium: '👑 프리미엄 모델' };
              return (
                <div key={tier} className="mb-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">{tierLabels[tier]}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {tierModels.map(m => (
                      <button
                        key={m.key}
                        className={`p-3 rounded-xl border-2 transition-all text-left relative ${
                          model === m.key
                            ? m.provider === 'openai'
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-orange-500 bg-orange-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setModel(m.key)}
                      >
                        {/* 뱃지 */}
                        {m.badge && (
                          <span className={`absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                            m.badge === '추천' || m.badge === '가성비' ? 'bg-violet-500 text-white' :
                            m.badge === '최저가' ? 'bg-green-500 text-white' :
                            m.badge === '고품질' || m.badge === '균형' ? 'bg-blue-500 text-white' :
                            m.badge === '최고품질' ? 'bg-amber-500 text-white' : 'bg-slate-500 text-white'
                          }`}>
                            {m.badge}
                          </span>
                        )}

                        <div className="flex items-start gap-2">
                          <span className="text-lg">{m.provider === 'openai' ? '🤖' : '🧠'}</span>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm ${
                              model === m.key
                                ? m.provider === 'openai' ? 'text-emerald-700' : 'text-orange-700'
                                : 'text-slate-700'
                            }`}>
                              {m.name}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {/* 가격 */}
                              <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                                {m.price}
                              </span>
                              {/* 품질 */}
                              <span className="text-[10px] flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <span key={i} className={i < m.quality ? 'text-amber-400' : 'text-slate-200'}>★</span>
                                ))}
                                <span className="text-slate-500 ml-0.5">{m.qualityLabel}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 템플릿 저장/불러오기 */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-700">📋 템플릿</span>
              <button
                className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                onClick={() => setShowSaveTemplate(!showSaveTemplate)}
              >
                {showSaveTemplate ? '취소' : '+ 현재 설정 저장'}
              </button>
            </div>

            {showSaveTemplate && (
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="템플릿 이름"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  className="input flex-1 text-sm py-2"
                />
                <button
                  className="btn btn-primary text-xs py-2"
                  onClick={async () => {
                    if (!templateName.trim()) {
                      toast.error('템플릿 이름을 입력하세요');
                      return;
                    }
                    try {
                      await api.saveTemplate({ name: templateName, site, style, length, model });
                      toast.success('템플릿 저장됨');
                      setTemplateName('');
                      setShowSaveTemplate(false);
                      const res = await api.getTemplates();
                      setTemplates(res.data.templates || []);
                    } catch (error) {
                      toast.error('저장 실패');
                    }
                  }}
                >
                  <Save size={14} /> 저장
                </button>
              </div>
            )}

            {templates.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    className="px-3 py-1.5 rounded-lg text-xs bg-white border border-slate-200 hover:border-violet-300 flex items-center gap-2 group"
                    onClick={() => {
                      if (t.site) setSite(t.site);
                      setStyle(t.style);
                      setLength(t.length);
                      setModel(t.model);
                      toast.success(`"${t.name}" 템플릿 적용됨`);
                    }}
                  >
                    <span>{t.name}</span>
                    <X
                      size={12}
                      className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await api.deleteTemplate(t.id);
                        const res = await api.getTemplates();
                        setTemplates(res.data.templates || []);
                      }}
                    />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">저장된 템플릿이 없습니다</p>
            )}
          </div>

          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 text-sm text-slate-600">
            <p className="flex items-start gap-2">
              <span className="text-lg">💡</span>
              <span>선택한 키워드별로 각각 1개씩 글이 생성됩니다. 생성된 글은 '대기중' 상태로 저장됩니다.</span>
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex justify-between items-center">
          <span className="text-sm text-slate-500">
            {selectedKeywords.length}개 키워드 · {WRITING_STYLES.find(s => s.id === style)?.name} · {LENGTH_OPTIONS.find(l => l.id === length)?.name} · {models.find(m => m.key === model)?.name || model}
          </span>
          <div className="flex gap-3">
            <button className="btn btn-secondary" onClick={onClose}>취소</button>
            <button
              className="btn btn-secondary"
              onClick={async () => {
                if (selectedKeywords.length !== 1) {
                  toast.error('A/B 테스트는 1개 키워드만 선택하세요');
                  return;
                }
                try {
                  const res = await api.createAbTest({
                    keywordId: selectedKeywords[0].id,
                    styles: ['info', 'review'],
                    length,
                    model
                  });
                  toast.success(`A/B 테스트 생성됨! (${res.data.testId})`);
                } catch (error) {
                  toast.error('A/B 테스트 생성 실패');
                }
              }}
              disabled={selectedKeywords.length !== 1}
            >
              <Zap size={16} />
              A/B 테스트
            </button>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading || selectedKeywords.length === 0}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  생성 중...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  {selectedKeywords.length}개 생성하기
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 글 상세 모달 (HTML 복사, 재생성, 수정)
// ============================================

function PostDetailModal({ post, isOpen, onClose, onStatusChange, onThumbnailClick, onExternalPublish }) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [isEditing, setIsEditing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editData, setEditData] = useState({ title: '', content_md: '', hashtags: [] });
  const [regenStyle, setRegenStyle] = useState('info');
  const [regenLength, setRegenLength] = useState('medium');
  const [showRegenOptions, setShowRegenOptions] = useState(false);
  const [seoScore, setSeoScore] = useState(null);
  const [loadingSeo, setLoadingSeo] = useState(false);

  // post가 변경되면 편집 데이터 초기화
  useEffect(() => {
    if (post) {
      setEditData({
        title: post.title || '',
        content_md: post.content_md || '',
        hashtags: post.hashtags || []
      });
      setIsEditing(false);
      setShowRegenOptions(false);
      setSeoScore(null);

      // SEO 점수 로드
      setLoadingSeo(true);
      api.getSeoScore(post.id)
        .then(res => setSeoScore(res.data))
        .catch(() => setSeoScore(null))
        .finally(() => setLoadingSeo(false));
    }
  }, [post]);

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(post.content_html);
      setCopied(true);
      toast.success('HTML 복사됨!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('복사 실패');
    }
  };

  const handlePublish = async () => {
    await api.updateStatus(post.id, 'published');
    toast.success('발행 완료 처리됨');
    onStatusChange();
    onClose();
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const res = await api.regeneratePost(post.id, { style: regenStyle, length: regenLength });
      toast.success('글이 재생성되었습니다!');
      setEditData({
        title: res.data.title,
        content_md: res.data.content_md,
        hashtags: res.data.hashtags
      });
      setShowRegenOptions(false);
      onStatusChange();
    } catch (error) {
      toast.error('재생성 실패: ' + error.message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.updatePost(post.id, editData);
      toast.success('저장되었습니다!');
      setIsEditing(false);
      onStatusChange();
    } catch (error) {
      toast.error('저장 실패: ' + error.message);
    }
  };

  if (!isOpen || !post) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-start">
          <div className="flex flex-col gap-2 flex-1">
            <div className="flex gap-2">
              <span className={`badge ${post.site === 'finance' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                {post.site === 'finance' ? '💰 금융' : '🔮 운세'}
              </span>
              <span className="badge bg-slate-100 text-slate-600">{post.keyword}</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={editData.title}
                onChange={e => setEditData({ ...editData, title: e.target.value })}
                className="input text-lg font-bold"
              />
            ) : (
              <h2 className="text-lg font-bold text-slate-800">{post.title}</h2>
            )}
            {seoScore && (
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                  seoScore.grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                  seoScore.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                  seoScore.grade === 'C' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  SEO {seoScore.grade}등급
                </span>
                <span className="text-sm text-slate-500">{seoScore.totalScore}/100점</span>
              </div>
            )}
            {/* 생성 정보 표시 */}
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
              {post.generation_model && (
                <span className="px-2 py-1 bg-slate-100 rounded">
                  🤖 {post.generation_model}
                </span>
              )}
              {post.generation_tokens > 0 && (
                <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">
                  📊 {post.generation_tokens?.toLocaleString()} tokens
                </span>
              )}
              {post.generation_cost > 0 && (
                <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded">
                  💰 ${Number(post.generation_cost).toFixed(4)}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-slate-100 px-6">
          <button
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'preview'
                ? 'text-violet-600 border-violet-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('preview')}
          >
            <Eye size={16} /> 미리보기
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'markdown'
                ? 'text-violet-600 border-violet-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('markdown')}
          >
            <Edit2 size={16} /> 마크다운
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'html'
                ? 'text-violet-600 border-violet-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('html')}
          >
            <FileText size={16} /> HTML 코드
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'preview' ? (
            <div
              className="prose max-w-none bg-slate-50 rounded-xl p-6 border border-slate-200"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
            />
          ) : activeTab === 'markdown' ? (
            isEditing ? (
              <textarea
                value={editData.content_md}
                onChange={e => setEditData({ ...editData, content_md: e.target.value })}
                className="w-full h-96 p-4 bg-slate-50 rounded-xl border border-slate-200 font-mono text-sm resize-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            ) : (
              <pre className="bg-slate-50 rounded-xl p-6 border border-slate-200 text-sm font-mono overflow-x-auto whitespace-pre-wrap">
                {post.content_md}
              </pre>
            )
          ) : (
            <pre className="bg-slate-900 text-slate-100 rounded-xl p-5 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
              {post.content_html}
            </pre>
          )}

          {/* 재생성 옵션 */}
          {showRegenOptions && (
            <div className="mt-6 bg-violet-50 rounded-xl p-5 border border-violet-200">
              <h4 className="font-semibold text-violet-800 mb-4">재생성 옵션</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">글 스타일</label>
                  <select
                    value={regenStyle}
                    onChange={e => setRegenStyle(e.target.value)}
                    className="select w-full"
                  >
                    {WRITING_STYLES.map(s => (
                      <option key={s.id} value={s.id}>{s.icon} {s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">글 길이</label>
                  <select
                    value={regenLength}
                    onChange={e => setRegenLength(e.target.value)}
                    className="select w-full"
                  >
                    {LENGTH_OPTIONS.map(l => (
                      <option key={l.id} value={l.id}>{l.icon} {l.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  className="btn btn-primary"
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      재생성 중...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      재생성하기
                    </>
                  )}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRegenOptions(false)}
                >
                  취소
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex justify-between items-center">
          <span className="text-sm text-slate-500 truncate max-w-md">
            🔗 {post.url}
          </span>
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button className="btn btn-primary" onClick={handleSave}>
                  <Check size={16} />
                  저장
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  setIsEditing(false);
                  setEditData({ title: post.title, content_md: post.content_md, hashtags: post.hashtags });
                }}>
                  취소
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    const time = prompt('예약 시간 입력 (예: 2025-01-15 09:00)');
                    if (time) {
                      try {
                        await api.schedulePost(post.id, time);
                        toast.success('예약 발행 설정됨');
                        onStatusChange();
                      } catch (error) {
                        toast.error('예약 실패');
                      }
                    }
                  }}
                >
                  <Calendar size={16} />
                  예약
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    const prompt = post.keyword + ' 관련 블로그 썸네일 이미지, 깔끔한 일러스트 스타일';
                    try {
                      toast.loading('이미지 생성 중...');
                      const res = await api.generateImage(prompt, '1024x1024');
                      await api.addImageToPost(post.id, res.data.imageUrl);
                      toast.dismiss();
                      toast.success('이미지 생성 완료!');
                      onStatusChange();
                    } catch (error) {
                      toast.dismiss();
                      toast.error('이미지 생성 실패: ' + error.message);
                    }
                  }}
                >
                  <Image size={16} />
                  이미지
                </button>
                <button className="btn btn-secondary" onClick={() => setShowRegenOptions(!showRegenOptions)}>
                  <RefreshCw size={16} />
                  재생성
                </button>
                <button className="btn btn-secondary" onClick={() => { setIsEditing(true); setActiveTab('markdown'); }}>
                  <Edit2 size={16} />
                  수정
                </button>
                <button className="btn btn-secondary" onClick={copyHtml}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? '복사됨!' : 'HTML 복사'}
                </button>
                <button className="btn btn-secondary" onClick={onThumbnailClick}>
                  <Image size={16} />
                  썸네일
                </button>
                <button className="btn btn-secondary" onClick={onExternalPublish}>
                  <Globe size={16} />
                  외부 발행
                </button>
                {post.status === 'draft' && (
                  <button className="btn btn-primary" onClick={handlePublish}>
                    <Send size={16} />
                    발행 완료
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}




// ============================================
// 대시보드 모달
// ============================================

function DashboardModal({ isOpen, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      api.getDashboard()
        .then(res => setData(res.data))
        .catch(() => toast.error('대시보드 로드 실패'))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <LayoutDashboard className="text-violet-500" size={24} />
            성과 대시보드
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <RefreshCw size={32} className="animate-spin text-slate-400" />
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* 개요 카드 */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{data.overview.total}</p>
                  <p className="text-sm text-blue-600/70">전체 글</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{data.overview.draft}</p>
                  <p className="text-sm text-amber-600/70">대기중</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{data.overview.published}</p>
                  <p className="text-sm text-emerald-600/70">발행됨</p>
                </div>
                <div className="bg-violet-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-violet-600">{data.overview.scheduled}</p>
                  <p className="text-sm text-violet-600/70">예약됨</p>
                </div>
              </div>

              {/* 비용 통계 */}
              {data.costs && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-rose-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-rose-600">${Number(data.costs.total || 0).toFixed(4)}</p>
                    <p className="text-sm text-rose-600/70">총 비용</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">${Number(data.costs.today || 0).toFixed(4)}</p>
                    <p className="text-sm text-orange-600/70">오늘 비용</p>
                  </div>
                  <div className="bg-cyan-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-cyan-600">{(data.costs.totalTokens || 0).toLocaleString()}</p>
                    <p className="text-sm text-cyan-600/70">총 토큰</p>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-teal-600">
                      ${data.overview.total > 0 ? (Number(data.costs.total || 0) / data.overview.total).toFixed(4) : '0'}
                    </p>
                    <p className="text-sm text-teal-600/70">글당 평균</p>
                  </div>
                </div>
              )}

              {/* 주간 생성량 */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3">📊 주간 생성량</h3>
                <div className="flex items-end gap-2 h-32">
                  {data.weeklyPosts.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-violet-500 rounded-t"
                        style={{ height: `${Math.max(d.count * 20, 4)}px` }}
                      ></div>
                      <span className="text-xs text-slate-500 mt-1">{new Date(d.date).getDate()}일</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 사이트별/카테고리별 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3">🌐 사이트별</h3>
                  {data.bySite.map(s => (
                    <div key={s.site} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <span className="mr-1">{s.icon || '🌐'}</span>
                        <span className="font-medium">{s.site_name || s.site}</span>
                        <span className="text-xs text-slate-400 ml-1">({s.site})</span>
                      </div>
                      <div className="text-right text-sm">
                        <span className="font-medium">{s.count}개</span>
                        {s.total_cost > 0 && (
                          <span className="text-amber-500 ml-2">${Number(s.total_cost).toFixed(4)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3">📁 카테고리별</h3>
                  {data.byCategory.slice(0, 5).map(c => (
                    <div key={c.category} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <span className="font-medium">{c.category_name || c.category}</span>
                        <span className="text-xs text-slate-400 ml-1">({c.category})</span>
                      </div>
                      <div className="text-right text-sm">
                        <span className="font-medium">{c.count}개</span>
                        {c.total_cost > 0 && (
                          <span className="text-amber-500 ml-2">${Number(c.total_cost).toFixed(4)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 비용 추적 모달
// ============================================

function CostModal({ isOpen, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      api.getCosts('day')
        .then(res => setData(res.data))
        .catch(() => toast.error('비용 데이터 로드 실패'))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="text-emerald-500" size={24} />
            API 비용 추적
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <RefreshCw size={32} className="animate-spin text-slate-400" />
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* 총 비용 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">${Number(data.total?.cost || 0).toFixed(4)}</p>
                  <p className="text-sm text-emerald-600/70">총 비용</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">${Number(data.today || 0).toFixed(4)}</p>
                  <p className="text-sm text-blue-600/70">오늘 비용</p>
                </div>
              </div>

              {/* 모델별 비용 */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3">🤖 모델별 비용</h3>
                {data.byModel?.map(m => (
                  <div key={m.model} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                    <div>
                      <span className="font-medium">{m.model}</span>
                      <span className="text-sm text-slate-500 ml-2">({m.calls}회)</span>
                    </div>
                    <span className="font-bold text-emerald-600">${Number(m.cost || 0).toFixed(4)}</span>
                  </div>
                ))}
              </div>

              {/* 일별 비용 */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3">📅 최근 비용</h3>
                <div className="max-h-40 overflow-y-auto">
                  {data.byDate?.map(d => (
                    <div key={d.date} className="flex justify-between py-1">
                      <span>{new Date(d.date).toLocaleDateString('ko-KR')}</span>
                      <span className="font-medium">${Number(d.cost || 0).toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 스케줄러 모달
// ============================================

function SchedulerModal({ isOpen, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    name: '', site: 'finance', category: '', count: 5,
    style: 'info', length: 'medium', model: 'gpt-4o-mini',
    schedule_type: 'daily', schedule_time: '09:00'
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsRes, sitesRes] = await Promise.all([
        api.getSchedulerJobs(),
        api.getSites()
      ]);
      setJobs(jobsRes.data.jobs || []);
      setSites(sitesRes.data.sites || []);
    } catch (error) {
      toast.error('데이터 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen]);

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('작업 이름을 입력하세요');
      return;
    }
    try {
      await api.createSchedulerJob(formData);
      toast.success('자동화 작업 생성됨');
      setShowAdd(false);
      setFormData({ ...formData, name: '' });
      loadData();
    } catch (error) {
      toast.error('생성 실패');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Clock className="text-violet-500" size={24} />
            자동화 스케줄러
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <button
            className="btn btn-primary mb-4"
            onClick={() => setShowAdd(!showAdd)}
          >
            <Plus size={16} /> 새 작업 추가
          </button>

          {showAdd && (
            <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
              <input
                type="text"
                placeholder="작업 이름 (예: 매일 금융 글 5개)"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="input"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={formData.site}
                  onChange={e => setFormData({ ...formData, site: e.target.value })}
                  className="select"
                >
                  {sites.map(s => (
                    <option key={s.id} value={s.site_key}>{s.site_name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="생성 개수"
                  value={formData.count}
                  onChange={e => setFormData({ ...formData, count: parseInt(e.target.value) })}
                  className="input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={formData.schedule_type}
                  onChange={e => setFormData({ ...formData, schedule_type: e.target.value })}
                  className="select"
                >
                  <option value="daily">매일</option>
                  <option value="weekly">매주</option>
                  <option value="manual">수동</option>
                </select>
                <input
                  type="time"
                  value={formData.schedule_time}
                  onChange={e => setFormData({ ...formData, schedule_time: e.target.value })}
                  className="input"
                />
              </div>
              <button className="btn btn-primary" onClick={handleCreate}>
                <Check size={16} /> 생성
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <RefreshCw size={32} className="animate-spin text-slate-400" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Clock size={48} className="mx-auto mb-4" />
              <p>등록된 자동화 작업이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map(job => (
                <div key={job.id} className={`rounded-xl p-4 border ${job.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-slate-800">{job.name}</h4>
                      <p className="text-sm text-slate-500">
                        {job.site} · {job.count}개 · {job.style} · {job.schedule_type} {job.schedule_time}
                      </p>
                      <div className="flex gap-3 mt-1 text-xs">
                        {job.run_count > 0 && (
                          <span className="text-blue-500">🔄 {job.run_count}회 실행</span>
                        )}
                        {job.total_posts > 0 && (
                          <span className="text-emerald-500">📝 {job.total_posts}개 생성</span>
                        )}
                        {job.total_cost > 0 && (
                          <span className="text-amber-500">💰 ${Number(job.total_cost).toFixed(4)}</span>
                        )}
                      </div>
                      {job.last_run && (
                        <p className="text-xs text-slate-400 mt-1">
                          마지막 실행: {new Date(job.last_run).toLocaleString('ko-KR')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"
                        onClick={async () => {
                          try {
                            toast.loading('글 생성 중...');
                            const res = await api.runSchedulerJob(job.id);
                            toast.dismiss();
                            if (res.data.success) {
                              toast.success(`${res.data.generated}개 글 생성! (비용: $${res.data.totalCost})`);
                            } else {
                              toast.error(res.data.message || '실행 실패');
                            }
                            loadData();
                          } catch (error) {
                            toast.dismiss();
                            toast.error('실행 실패');
                          }
                        }}
                      >
                        <Play size={16} />
                      </button>
                      <button
                        className="p-2 rounded-lg hover:bg-slate-100"
                        onClick={async () => {
                          await api.toggleSchedulerJob(job.id);
                          loadData();
                        }}
                      >
                        {job.is_active ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button
                        className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                        onClick={async () => {
                          if (window.confirm('삭제하시겠습니까?')) {
                            await api.deleteSchedulerJob(job.id);
                            loadData();
                          }
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 키워드 관리 모달
// ============================================

function KeywordManageModal({ isOpen, onClose }) {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('finance');
  const [keywords, setKeywords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [showAddSiteForm, setShowAddSiteForm] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ keyword: '', category: '', url: '' });
  const [categoryFormData, setCategoryFormData] = useState({ category_key: '', category_name: '' });
  const [siteFormData, setSiteFormData] = useState({ site_key: '', site_name: '', base_url: '', icon: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [sitesRes, kwRes, catRes] = await Promise.all([
        api.getSites(),
        axios.get(`${API_URL}/keywords`, { params: { site: selectedSite, include_inactive: 'true' } }),
        api.getCategories(selectedSite)
      ]);
      setSites(sitesRes.data.sites || []);
      setKeywords(kwRes.data.keywords || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      toast.error('데이터 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, selectedSite]);

  const handleUpdateSite = async (id) => {
    try {
      await api.updateSite(id, siteFormData);
      toast.success('사이트 수정됨');
      setEditingSiteId(null);
      loadData();
    } catch (error) {
      toast.error('수정 실패');
    }
  };

  const startEditSite = (site) => {
    setEditingSiteId(site.id);
    setSiteFormData({ site_name: site.site_name, base_url: site.base_url, icon: site.icon });
  };

  const handleAddSite = async () => {
    if (!siteFormData.site_key || !siteFormData.site_name || !siteFormData.base_url) {
      toast.error('모든 필드를 입력하세요');
      return;
    }
    try {
      await api.addSite(siteFormData);
      toast.success('사이트 추가됨');
      setSiteFormData({ site_key: '', site_name: '', base_url: '', icon: '' });
      setShowAddSiteForm(false);
      loadData();
    } catch (error) {
      toast.error('추가 실패: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteSite = async (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await api.deleteSite(id);
        toast.success('삭제됨');
        const remainingSites = sites.filter(s => s.id !== id);
        if (remainingSites.length > 0) setSelectedSite(remainingSites[0].site_key);
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || '삭제 실패');
      }
    }
  };

  const handleAdd = async () => {
    if (!formData.keyword || !formData.category || !formData.url) {
      toast.error('모든 필드를 입력하세요');
      return;
    }
    try {
      await api.addKeyword({ site: selectedSite, ...formData });
      toast.success('키워드 추가됨');
      setFormData({ keyword: '', category: '', url: '' });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      toast.error('추가 실패: ' + error.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.updateKeyword(id, { ...formData, site: selectedSite });
      toast.success('수정됨');
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error('수정 실패');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await api.deleteKeyword(id);
        toast.success('삭제됨');
        loadData();
      } catch (error) {
        toast.error('삭제 실패');
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleKeyword(id);
      loadData();
    } catch (error) {
      toast.error('상태 변경 실패');
    }
  };

  const startEdit = (kw) => {
    setEditingId(kw.id);
    setFormData({ keyword: kw.keyword, category: kw.category, url: kw.url });
  };

  const handleAddCategory = async () => {
    if (!categoryFormData.category_key || !categoryFormData.category_name) {
      toast.error('모든 필드를 입력하세요');
      return;
    }
    try {
      await api.addCategory({ site: selectedSite, ...categoryFormData });
      toast.success('카테고리 추가됨');
      setCategoryFormData({ category_key: '', category_name: '' });
      setShowAddCategoryForm(false);
      loadData();
    } catch (error) {
      toast.error('추가 실패: ' + error.message);
    }
  };

  const handleDeleteCategory = async (id, categoryKey) => {
    const keywordCount = keywords.filter(k => k.category === categoryKey).length;
    if (keywordCount > 0) {
      toast.error(`이 카테고리에 ${keywordCount}개의 키워드가 있습니다. 먼저 키워드를 삭제하세요.`);
      return;
    }
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await api.deleteCategory(id);
        toast.success('삭제됨');
        loadData();
      } catch (error) {
        toast.error('삭제 실패');
      }
    }
  };

  if (!isOpen) return null;

  const grouped = {};
  keywords.forEach(kw => {
    if (!grouped[kw.category]) grouped[kw.category] = [];
    grouped[kw.category].push(kw);
  });

  const currentSite = sites.find(s => s.site_key === selectedSite);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Settings className="text-violet-500" size={24} />
            사이트 & 키워드 관리
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* 왼쪽: 사이트 목록 */}
          <div className="w-72 border-r border-slate-100 bg-slate-50 p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">사이트 목록</span>
              <button
                className="btn btn-primary text-xs py-1.5 px-3"
                onClick={() => setShowAddSiteForm(!showAddSiteForm)}
              >
                <Plus size={14} /> 추가
              </button>
            </div>

            {showAddSiteForm && (
              <div className="bg-white rounded-xl p-4 mb-4 border border-slate-200 space-y-3">
                <input
                  type="text"
                  placeholder="사이트 키 (영문)"
                  value={siteFormData.site_key}
                  onChange={e => setSiteFormData({ ...siteFormData, site_key: e.target.value })}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="아이콘 (이모지)"
                  value={siteFormData.icon}
                  onChange={e => setSiteFormData({ ...siteFormData, icon: e.target.value })}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="사이트 이름"
                  value={siteFormData.site_name}
                  onChange={e => setSiteFormData({ ...siteFormData, site_name: e.target.value })}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="URL"
                  value={siteFormData.base_url}
                  onChange={e => setSiteFormData({ ...siteFormData, base_url: e.target.value })}
                  className="input text-sm"
                />
                <div className="flex gap-2">
                  <button className="btn btn-primary flex-1 text-xs py-2" onClick={handleAddSite}>
                    <Check size={14} /> 추가
                  </button>
                  <button className="btn btn-secondary text-xs py-2" onClick={() => {
                    setShowAddSiteForm(false);
                    setSiteFormData({ site_key: '', site_name: '', base_url: '', icon: '' });
                  }}>
                    취소
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {sites.map(site => (
                <div
                  key={site.id}
                  className={`rounded-xl p-4 cursor-pointer transition-all ${
                    selectedSite === site.site_key
                      ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white border border-slate-200 hover:border-violet-300'
                  }`}
                  onClick={() => setSelectedSite(site.site_key)}
                >
                  {editingSiteId === site.id ? (
                    <div className="space-y-2" onClick={e => e.stopPropagation()}>
                      <input
                        type="text"
                        value={siteFormData.icon}
                        onChange={e => setSiteFormData({ ...siteFormData, icon: e.target.value })}
                        className="input text-sm"
                        placeholder="아이콘"
                      />
                      <input
                        type="text"
                        value={siteFormData.site_name}
                        onChange={e => setSiteFormData({ ...siteFormData, site_name: e.target.value })}
                        className="input text-sm"
                        placeholder="이름"
                      />
                      <input
                        type="text"
                        value={siteFormData.base_url}
                        onChange={e => setSiteFormData({ ...siteFormData, base_url: e.target.value })}
                        className="input text-sm"
                        placeholder="URL"
                      />
                      <div className="flex gap-2">
                        <button className="btn btn-primary flex-1 text-xs py-2" onClick={() => handleUpdateSite(site.id)}>
                          <Check size={14} /> 저장
                        </button>
                        <button className="btn btn-secondary text-xs py-2" onClick={() => setEditingSiteId(null)}>
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-lg font-medium">{site.icon} {site.site_name}</div>
                        <div className={`text-xs mt-1 ${selectedSite === site.site_key ? 'text-white/70' : 'text-slate-500'}`}>
                          {site.base_url}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          className={`p-2 rounded-lg transition-colors ${selectedSite === site.site_key ? 'hover:bg-white/20' : 'hover:bg-slate-100'}`}
                          onClick={(e) => { e.stopPropagation(); startEditSite(site); }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className={`p-2 rounded-lg transition-colors ${selectedSite === site.site_key ? 'hover:bg-white/20' : 'hover:bg-red-50 hover:text-red-500'}`}
                          onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.id); }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 오른쪽: 카테고리 & 키워드 */}
          <div className="flex-1 p-6 overflow-y-auto">
            {currentSite && (
              <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl p-4 mb-6 flex items-center gap-3">
                <span className="text-2xl">{currentSite.icon}</span>
                <div>
                  <div className="font-bold text-slate-800">{currentSite.site_name}</div>
                  <div className="text-sm text-slate-500">{currentSite.base_url}</div>
                </div>
              </div>
            )}

            <div className="flex gap-3 mb-6">
              {!showAddCategoryForm && !showAddForm && (
                <>
                  <button className="btn btn-secondary" onClick={() => setShowAddCategoryForm(true)}>
                    <Plus size={16} /> 카테고리 추가
                  </button>
                  <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
                    <Plus size={16} /> 키워드 추가
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      const baseKeyword = prompt('기준 키워드를 입력하세요 (예: 연봉 계산)');
                      if (!baseKeyword) return;

                      toast.loading('AI가 키워드를 추천 중...');
                      try {
                        const res = await api.recommendKeywords({
                          baseKeyword,
                          site: selectedSite,
                          count: 10
                        });
                        toast.dismiss();

                        const keywords = res.data.keywords || [];
                        if (keywords.length > 0) {
                          const msg = keywords.map(k =>
                            `• ${k.keyword} (${k.difficulty}) - ${k.reason}`
                          ).join('\n');
                          alert('추천 키워드:\n\n' + msg);
                        } else {
                          toast.error('추천 키워드가 없습니다');
                        }
                      } catch (error) {
                        toast.dismiss();
                        toast.error('키워드 추천 실패');
                      }
                    }}
                  >
                    <Sparkles size={16} /> AI 추천
                  </button>
                  <label className="btn btn-secondary cursor-pointer">
                    <Upload size={16} /> CSV 가져오기
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('site', selectedSite);

                        try {
                          const res = await api.importKeywords(formData);
                          toast.success(res.data.message);
                          if (res.data.errors?.length > 0) {
                            console.log('Import errors:', res.data.errors);
                          }
                          loadData();
                        } catch (error) {
                          toast.error('CSV 가져오기 실패: ' + (error.response?.data?.error || error.message));
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                </>
              )}
            </div>

            {showAddCategoryForm && (
              <div className="bg-slate-50 rounded-xl p-5 mb-6 border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-4">새 카테고리 추가</h4>
                <div className="grid gap-3">
                  <input
                    type="text"
                    placeholder="카테고리 키 (영문, 예: salary)"
                    value={categoryFormData.category_key}
                    onChange={e => setCategoryFormData({ ...categoryFormData, category_key: e.target.value })}
                    className="input"
                  />
                  <input
                    type="text"
                    placeholder="카테고리 이름 (예: 연봉/급여)"
                    value={categoryFormData.category_name}
                    onChange={e => setCategoryFormData({ ...categoryFormData, category_name: e.target.value })}
                    className="input"
                  />
                  <div className="flex gap-3">
                    <button className="btn btn-primary" onClick={handleAddCategory}>
                      <Check size={16} /> 추가
                    </button>
                    <button className="btn btn-secondary" onClick={() => {
                      setShowAddCategoryForm(false);
                      setCategoryFormData({ category_key: '', category_name: '' });
                    }}>
                      취소
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showAddForm && (
              <div className="bg-slate-50 rounded-xl p-5 mb-6 border border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-4">새 키워드 추가</h4>
                <div className="grid gap-3">
                  <input
                    type="text"
                    placeholder="키워드 (예: 연봉 1억 실수령액)"
                    value={formData.keyword}
                    onChange={e => setFormData({ ...formData, keyword: e.target.value })}
                    className="input"
                  />
                  <select
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                    className="select"
                  >
                    <option value="">카테고리 선택</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.category_key}>
                        {cat.category_name} ({cat.category_key})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="URL 경로 (예: /salary/yearly/10000)"
                    value={formData.url}
                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                    className="input"
                  />
                  <div className="flex gap-3">
                    <button className="btn btn-primary" onClick={handleAdd}>
                      <Check size={16} /> 추가
                    </button>
                    <button className="btn btn-secondary" onClick={() => {
                      setShowAddForm(false);
                      setFormData({ keyword: '', category: '', url: '' });
                    }}>
                      취소
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <RefreshCw size={32} className="animate-spin" />
                <p className="mt-4">로딩 중...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(grouped).map(([category, kws]) => {
                  const cat = categories.find(c => c.category_key === category);
                  return (
                    <div key={category}>
                      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl px-4 py-3 mb-3 flex justify-between items-center">
                        <span className="font-semibold">
                          {cat?.category_name || category}
                          <span className="ml-2 text-white/70 text-sm font-normal">
                            ({kws.length}개)
                          </span>
                        </span>
                        {cat && (
                          <button
                            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                            onClick={() => handleDeleteCategory(cat.id, category)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {kws.map(kw => (
                          <div
                            key={kw.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                              kw.is_active
                                ? 'bg-white border-slate-200 hover:border-violet-300'
                                : 'bg-slate-50 border-slate-100 opacity-60'
                            }`}
                          >
                            {editingId === kw.id ? (
                              <>
                                <input
                                  type="text"
                                  value={formData.keyword}
                                  onChange={e => setFormData({ ...formData, keyword: e.target.value })}
                                  className="input flex-1 text-sm"
                                />
                                <input
                                  type="text"
                                  value={formData.url}
                                  onChange={e => setFormData({ ...formData, url: e.target.value })}
                                  className="input w-48 text-sm"
                                />
                                <button className="btn btn-primary text-xs py-2" onClick={() => handleUpdate(kw.id)}>
                                  <Check size={14} />
                                </button>
                                <button className="btn btn-secondary text-xs py-2" onClick={() => setEditingId(null)}>
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="flex-1 font-medium text-slate-800">{kw.keyword}</span>
                                {kw.post_count !== undefined && (
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    kw.post_count > 0
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-slate-100 text-slate-500'
                                  }`}>
                                    {kw.post_count}회 사용
                                  </span>
                                )}
                                <span className="text-sm text-slate-500 max-w-xs truncate">{kw.url}</span>
                                <button
                                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                                  onClick={() => handleToggle(kw.id)}
                                >
                                  {kw.is_active ? (
                                    <ToggleRight size={20} className="text-emerald-500" />
                                  ) : (
                                    <ToggleLeft size={20} className="text-slate-400" />
                                  )}
                                </button>
                                <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => startEdit(kw)}>
                                  <Edit2 size={16} className="text-slate-500" />
                                </button>
                                <button className="p-2 rounded-lg hover:bg-red-50 transition-colors" onClick={() => handleDelete(kw.id)}>
                                  <Trash2 size={16} className="text-slate-500 hover:text-red-500" />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && keywords.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Tag size={48} />
                <p className="mt-4">등록된 키워드가 없습니다.</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50">
          <span className="text-sm text-slate-500">
            {categories.length}개 카테고리 · {keywords.length}개 키워드 (활성: {keywords.filter(k => k.is_active).length}개)
          </span>
          <button className="btn btn-secondary" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 콘텐츠 캘린더 컴포넌트
// ============================================

function ContentCalendar({ onClose, onSelectPost }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendar, setCalendar] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCalendar();
  }, [currentDate]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const { data } = await api.getCalendar(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1
      );
      setCalendar(data.calendar || {});
    } catch (error) {
      toast.error('캘린더 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const getDateKey = (day) => {
    if (!day) return null;
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-${String(day).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="text-indigo-500" /> 콘텐츠 캘린더
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronLeft size={20} />
            </button>
            <h3 className="text-lg font-semibold">
              {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
            </h3>
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['일', '월', '화', '수', '목', '금', '토'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-indigo-500" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day, idx) => {
                const dateKey = getDateKey(day);
                const posts = dateKey ? (calendar[dateKey] || []) : [];
                const isToday = day && new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-2 border rounded-lg ${
                      day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                    } ${isToday ? 'ring-2 ring-indigo-500' : ''}`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-indigo-600' : 'text-slate-700'}`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {posts.slice(0, 3).map(post => (
                            <div
                              key={post.id}
                              onClick={() => onSelectPost(post)}
                              className={`text-xs p-1 rounded truncate cursor-pointer ${
                                post.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                                post.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-600'
                              }`}
                              title={post.title}
                            >
                              {post.title}
                            </div>
                          ))}
                          {posts.length > 3 && (
                            <div className="text-xs text-slate-400">+{posts.length - 3}개</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 키워드 성과 분석 컴포넌트 (차트 포함)
// ============================================

const GRADE_COLORS = {
  A: '#10b981',
  B: '#3b82f6',
  C: '#f59e0b',
  D: '#ef4444'
};

function KeywordAnalytics({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const { data } = await api.getKeywordAnalytics();
      setData(data);
    } catch (error) {
      toast.error('분석 데이터 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A': return 'bg-emerald-100 text-emerald-700';
      case 'B': return 'bg-blue-100 text-blue-700';
      case 'C': return 'bg-amber-100 text-amber-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  // 차트 데이터 변환
  const gradeChartData = data?.gradeDistribution?.map(item => ({
    name: `${item.seo_grade}등급`,
    value: item.count,
    grade: item.seo_grade
  })) || [];

  const siteChartData = data?.siteStats?.map(site => ({
    name: site.site === 'finance' ? '금융' : '운세',
    게시글: site.post_count,
    'SEO점수': Math.round(site.avg_seo_score || 0),
    비용: Number(site.total_cost || 0)
  })) || [];

  const topKeywordsData = data?.keywordStats?.slice(0, 10).map(kw => ({
    name: kw.keyword.length > 10 ? kw.keyword.substring(0, 10) + '...' : kw.keyword,
    fullName: kw.keyword,
    게시글: kw.post_count,
    'SEO점수': Math.round(kw.avg_seo_score || 0)
  })) || [];

  // 주간 생성량 라인차트 데이터
  const weeklyChartData = data?.weeklyStats?.map(item => ({
    week: item.week_start ? new Date(item.week_start).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }) : '',
    생성량: item.count,
    'SEO평균': Math.round(item.avg_seo || 0),
    비용: Number(item.cost || 0).toFixed(3)
  })) || [];

  // 일별 생성량 라인차트 데이터
  const dailyChartData = data?.dailyStats?.map(item => ({
    date: item.date ? new Date(item.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }) : '',
    생성량: item.count,
    'SEO평균': Math.round(item.avg_seo || 0),
    비용: Number(item.cost || 0).toFixed(3)
  })) || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <PieChart className="text-purple-500" /> 키워드 성과 분석
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'overview' ? 'bg-white shadow text-purple-600' : 'text-slate-600'
                }`}
              >
                차트
              </button>
              <button
                onClick={() => setActiveTab('table')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'table' ? 'bg-white shadow text-purple-600' : 'text-slate-600'
                }`}
              >
                상세 테이블
              </button>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-purple-500" size={32} />
            </div>
          ) : data && activeTab === 'overview' ? (
            <div className="space-y-8">
              {/* 상단 통계 카드 */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 rounded-xl">
                  <div className="text-sm opacity-80">A등급</div>
                  <div className="text-3xl font-bold">{data.gradeDistribution?.find(g => g.seo_grade === 'A')?.count || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-xl">
                  <div className="text-sm opacity-80">B등급</div>
                  <div className="text-3xl font-bold">{data.gradeDistribution?.find(g => g.seo_grade === 'B')?.count || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white p-4 rounded-xl">
                  <div className="text-sm opacity-80">C등급</div>
                  <div className="text-3xl font-bold">{data.gradeDistribution?.find(g => g.seo_grade === 'C')?.count || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-4 rounded-xl">
                  <div className="text-sm opacity-80">D등급</div>
                  <div className="text-3xl font-bold">{data.gradeDistribution?.find(g => g.seo_grade === 'D')?.count || 0}</div>
                </div>
              </div>

              {/* 차트 영역 */}
              <div className="grid grid-cols-2 gap-6">
                {/* SEO 등급 분포 파이차트 */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">SEO 등급 분포</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={gradeChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {gradeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={GRADE_COLORS[entry.grade]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>

                {/* 사이트별 성과 바차트 */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">사이트별 게시글 수 & SEO 점수</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={siteChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8b5cf6" />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="게시글" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="SEO점수" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 상위 키워드 바차트 */}
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">상위 10개 키워드 성과</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topKeywordsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      labelFormatter={(label) => topKeywordsData.find(k => k.name === label)?.fullName || label}
                    />
                    <Legend />
                    <Bar dataKey="게시글" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="SEO점수" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* 주간/일별 생성량 추이 */}
              <div className="grid grid-cols-2 gap-6">
                {/* 일별 생성량 (최근 30일) */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">📅 일별 생성량 추이 (최근 30일)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={dailyChartData}>
                      <defs>
                        <linearGradient id="colorDaily" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="생성량" stroke="#8b5cf6" strokeWidth={2} fill="url(#colorDaily)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* 주간 생성량 (최근 4주/1개월) */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">📈 주간 생성량 추이 (최근 1개월)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={weeklyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" orientation="left" stroke="#8b5cf6" />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="생성량" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                      <Line yAxisId="right" type="monotone" dataKey="SEO평균" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 사이트별 카드 */}
              <div>
                <h3 className="text-lg font-semibold mb-4">사이트별 상세</h3>
                <div className="grid grid-cols-2 gap-4">
                  {data.siteStats?.map(site => (
                    <div key={site.site} className="p-5 bg-slate-50 rounded-xl">
                      <div className="font-semibold text-lg mb-3">{site.site === 'finance' ? '💰 금융' : '🔮 운세'}</div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-slate-500">총 게시글</div>
                          <div className="text-xl font-bold text-purple-600">{site.post_count}개</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-slate-500">평균 SEO</div>
                          <div className="text-xl font-bold text-emerald-600">{Math.round(site.avg_seo_score || 0)}점</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-slate-500">총 비용</div>
                          <div className="text-xl font-bold text-amber-600">${Number(site.total_cost || 0).toFixed(4)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : data && activeTab === 'table' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">키워드별 상세 성과</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 rounded-tl-lg">키워드</th>
                      <th className="text-center p-3">게시글</th>
                      <th className="text-center p-3">평균 SEO</th>
                      <th className="text-center p-3">최고/최저</th>
                      <th className="text-center p-3">등급 분포</th>
                      <th className="text-right p-3 rounded-tr-lg">총 비용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.keywordStats?.map((kw, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-3 font-medium">{kw.keyword}</td>
                        <td className="p-3 text-center">{kw.post_count}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded ${
                            kw.avg_seo_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                            kw.avg_seo_score >= 60 ? 'bg-blue-100 text-blue-700' :
                            kw.avg_seo_score >= 40 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {Math.round(kw.avg_seo_score || 0)}점
                          </span>
                        </td>
                        <td className="p-3 text-center text-slate-500">
                          {kw.max_seo_score}/{kw.min_seo_score}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-1">
                            {kw.grade_a_count > 0 && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">A:{kw.grade_a_count}</span>}
                            {kw.grade_b_count > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">B:{kw.grade_b_count}</span>}
                            {kw.grade_c_count > 0 && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">C:{kw.grade_c_count}</span>}
                            {kw.grade_d_count > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">D:{kw.grade_d_count}</span>}
                          </div>
                        </td>
                        <td className="p-3 text-right">${Number(kw.total_cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 트렌드 키워드 추천 컴포넌트
// ============================================

function TrendKeywords({ onClose, onGenerate }) {
  const [site, setSite] = useState('finance');
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrends();
  }, [site]);

  const loadTrends = async () => {
    try {
      setLoading(true);
      const { data } = await api.getTrends(site);
      setTrends(data.trends || []);
    } catch (error) {
      toast.error('트렌드 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'up': return <ArrowUp className="text-emerald-500" size={16} />;
      case 'down': return <ArrowDown className="text-red-500" size={16} />;
      default: return <Minus className="text-slate-400" size={16} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <TrendingUp className="text-orange-500" /> 트렌드 키워드
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setSite('finance')}
              className={`px-4 py-2 rounded-lg font-medium ${
                site === 'finance' ? 'bg-blue-500 text-white' : 'bg-slate-100 hover:bg-slate-200'
              }`}
            >
              💰 금융
            </button>
            <button
              onClick={() => setSite('fortune')}
              className={`px-4 py-2 rounded-lg font-medium ${
                site === 'fortune' ? 'bg-pink-500 text-white' : 'bg-slate-100 hover:bg-slate-200'
              }`}
            >
              🔮 운세
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-orange-500" size={32} />
            </div>
          ) : (
            <div className="space-y-3">
              {trends.map((trend, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border ${
                    trend.used ? 'bg-slate-50 opacity-60' : 'bg-white hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{trend.keyword}</span>
                        {getTrendIcon(trend.trend)}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          trend.score >= 90 ? 'bg-red-100 text-red-700' :
                          trend.score >= 80 ? 'bg-orange-100 text-orange-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          인기도 {trend.score}
                        </span>
                        {trend.used && (
                          <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">
                            사용됨 ({trend.postCount}회)
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{trend.reason}</p>
                    </div>
                    <button
                      onClick={() => onGenerate(trend.keyword, site)}
                      disabled={trend.used}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        trend.used
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                    >
                      <Sparkles size={14} className="inline mr-1" />
                      생성
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// AI 썸네일 생성 컴포넌트
// ============================================

function ThumbnailGenerator({ post, onClose, onUpdate }) {
  const [style, setStyle] = useState('modern');
  const [generating, setGenerating] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(post.thumbnail_url);

  const styles = [
    { key: 'modern', label: '모던', desc: '깔끔하고 미니멀한 디자인' },
    { key: 'colorful', label: '컬러풀', desc: '화려하고 눈에 띄는 색상' },
    { key: 'professional', label: '프로페셔널', desc: '비즈니스/전문적인 느낌' },
    { key: 'cartoon', label: '카툰', desc: '친근한 일러스트 스타일' },
    { key: 'photo', label: '사진', desc: '사실적인 고품질 사진' },
  ];

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const { data } = await api.generateThumbnail(post.id, style);
      setThumbnailUrl(data.thumbnailUrl);
      toast.success(`썸네일 생성 완료 (비용: $${data.cost})`);
      onUpdate();
    } catch (error) {
      toast.error('썸네일 생성 실패: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Image className="text-pink-500" /> AI 썸네일 생성
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="text-sm text-slate-500 mb-1">키워드</div>
            <div className="font-medium">{post.keyword}</div>
          </div>

          <div className="mb-6">
            <div className="text-sm text-slate-500 mb-2">스타일 선택</div>
            <div className="grid grid-cols-5 gap-2">
              {styles.map(s => (
                <button
                  key={s.key}
                  onClick={() => setStyle(s.key)}
                  className={`p-3 rounded-xl text-center border-2 transition-all ${
                    style === s.key
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-medium text-sm">{s.label}</div>
                  <div className="text-xs text-slate-400 mt-1">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {thumbnailUrl && (
            <div className="mb-6">
              <div className="text-sm text-slate-500 mb-2">현재 썸네일</div>
              <img
                src={thumbnailUrl}
                alt="Thumbnail"
                className="w-full rounded-xl border"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 py-3 bg-pink-500 text-white rounded-xl font-medium hover:bg-pink-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generating ? (
                <RefreshCw className="animate-spin" size={18} />
              ) : (
                <Sparkles size={18} />
              )}
              {generating ? '생성 중...' : '썸네일 생성 ($0.04)'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-slate-100 rounded-xl font-medium hover:bg-slate-200"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 외부 사이트 발행 컴포넌트
// ============================================

function ExternalPublisher({ post, onClose }) {
  const [sites, setSites] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    platform: 'tistory',
    url: '',
    apiKey: '',
    apiSecret: '',
    blogId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sitesRes, logsRes] = await Promise.all([
        api.getExternalSites(),
        api.getPublishLogs(post.id)
      ]);
      setSites(sitesRes.data.sites || []);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      toast.error('데이터 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async () => {
    try {
      await api.addExternalSite(newSite);
      toast.success('사이트 추가됨');
      setShowAddForm(false);
      setNewSite({ name: '', platform: 'tistory', url: '', apiKey: '', apiSecret: '', blogId: '' });
      loadData();
    } catch (error) {
      toast.error('사이트 추가 실패');
    }
  };

  const handlePublish = async (siteId) => {
    try {
      setPublishing(siteId);
      await api.publishExternal(post.id, siteId);
      toast.success('발행 성공!');
      loadData();
    } catch (error) {
      toast.error('발행 실패: ' + error.response?.data?.error || error.message);
    } finally {
      setPublishing(null);
    }
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.deleteExternalSite(id);
      toast.success('삭제됨');
      loadData();
    } catch (error) {
      toast.error('삭제 실패');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Globe className="text-green-500" /> 외부 사이트 발행
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="mb-4 p-4 bg-slate-50 rounded-xl">
            <div className="text-sm text-slate-500 mb-1">발행할 글</div>
            <div className="font-medium">{post.title}</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw className="animate-spin text-green-500" size={32} />
            </div>
          ) : (
            <>
              {/* 연결된 사이트 목록 */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">연결된 사이트</h3>
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <Plus size={16} /> 사이트 추가
                  </button>
                </div>

                {showAddForm && (
                  <div className="mb-4 p-4 bg-green-50 rounded-xl space-y-3">
                    <input
                      placeholder="사이트 이름"
                      value={newSite.name}
                      onChange={e => setNewSite({...newSite, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    <select
                      value={newSite.platform}
                      onChange={e => setNewSite({...newSite, platform: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="tistory">티스토리</option>
                      <option value="wordpress">워드프레스</option>
                    </select>
                    {newSite.platform === 'wordpress' && (
                      <input
                        placeholder="사이트 URL (예: https://myblog.com)"
                        value={newSite.url}
                        onChange={e => setNewSite({...newSite, url: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    )}
                    <input
                      placeholder={newSite.platform === 'tistory' ? 'Access Token' : '사용자명'}
                      value={newSite.apiKey}
                      onChange={e => setNewSite({...newSite, apiKey: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    {newSite.platform === 'wordpress' && (
                      <input
                        placeholder="Application Password"
                        value={newSite.apiSecret}
                        onChange={e => setNewSite({...newSite, apiSecret: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        type="password"
                      />
                    )}
                    <input
                      placeholder={newSite.platform === 'tistory' ? '블로그 이름' : '블로그 ID (선택)'}
                      value={newSite.blogId}
                      onChange={e => setNewSite({...newSite, blogId: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddSite}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => setShowAddForm(false)}
                        className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}

                {sites.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    연결된 사이트가 없습니다. 사이트를 추가해주세요.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sites.map(site => (
                      <div key={site.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            site.platform === 'tistory' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {site.platform === 'tistory' ? '티스토리' : '워드프레스'}
                          </span>
                          <span className="font-medium">{site.name}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handlePublish(site.id)}
                            disabled={publishing === site.id}
                            className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                          >
                            {publishing === site.id ? (
                              <RefreshCw className="animate-spin" size={14} />
                            ) : (
                              <Send size={14} />
                            )}
                            발행
                          </button>
                          <button
                            onClick={() => handleDeleteSite(site.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 발행 기록 */}
              {logs.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">발행 기록</h3>
                  <div className="space-y-2">
                    {logs.map(log => (
                      <div key={log.id} className={`p-3 rounded-xl text-sm ${
                        log.status === 'success' ? 'bg-emerald-50' : 'bg-red-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              log.status === 'success' ? 'bg-emerald-200 text-emerald-700' : 'bg-red-200 text-red-700'
                            }`}>
                              {log.status === 'success' ? '성공' : '실패'}
                            </span>
                            <span>{log.site_name}</span>
                          </div>
                          <span className="text-slate-400">
                            {new Date(log.created_at).toLocaleString('ko-KR')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 로그인 페이지
// ============================================

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('아이디와 비밀번호를 입력하세요.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.login({ username, password });
      if (data.success) {
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success(`환영합니다, ${data.user.name}님!`);
        onLogin(data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || '로그인 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <FileText className="text-white" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">블로그 자동 생성기</h1>
          <p className="text-slate-500 mt-2">관리자 로그인</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">아이디</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="아이디 입력"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">비밀번호</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="비밀번호 입력"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <RefreshCw className="animate-spin" size={20} />
            ) : (
              <>
                <Lock size={20} />
                로그인
              </>
            )}
          </button>
        </form>

      </div>
    </div>
  );
}

// ============================================
// 사용자 관리 모달
// ============================================

function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    role: 'user',
    is_active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('사용자 목록 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      email: '',
      role: 'user',
      is_active: true
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: '',
      name: user.name || '',
      email: user.email || '',
      role: user.role,
      is_active: user.is_active
    });
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || (!editingUser && !formData.password)) {
      toast.error('필수 항목을 입력하세요.');
      return;
    }

    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
        toast.success('사용자 정보가 수정되었습니다.');
      } else {
        await api.addUser(formData);
        toast.success('사용자가 추가되었습니다.');
      }
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || '저장 실패');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.deleteUser(id);
      toast.success('삭제되었습니다.');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || '삭제 실패');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="text-purple-500" /> 사용자 관리
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg flex items-center gap-2 hover:bg-purple-600"
            >
              <UserPlus size={18} /> 사용자 추가
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {showForm && (
            <form onSubmit={handleSubmit} className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="font-semibold mb-4">{editingUser ? '사용자 수정' : '새 사용자 추가'}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">아이디 *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                    placeholder="아이디"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    비밀번호 {editingUser ? '(변경시만 입력)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                    placeholder="비밀번호"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">이름</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                    placeholder="이름"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">이메일</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                    placeholder="이메일"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">권한</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                  >
                    <option value="user">일반 사용자</option>
                    <option value="admin">관리자</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">상태</label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-purple-500"
                  >
                    <option value="active">활성</option>
                    <option value="inactive">비활성</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                >
                  {editingUser ? '수정' : '추가'}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-purple-500" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 rounded-tl-lg">아이디</th>
                    <th className="text-left p-3">이름</th>
                    <th className="text-left p-3">이메일</th>
                    <th className="text-center p-3">권한</th>
                    <th className="text-center p-3">상태</th>
                    <th className="text-center p-3">마지막 로그인</th>
                    <th className="text-right p-3 rounded-tr-lg">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-medium">{user.username}</td>
                      <td className="p-3">{user.name || '-'}</td>
                      <td className="p-3">{user.email || '-'}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {user.role === 'admin' ? '관리자' : '사용자'}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="p-3 text-center text-slate-500">
                        {user.last_login ? new Date(user.last_login).toLocaleString('ko-KR') : '-'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit2 size={14} />
                          </button>
                          {user.username !== 'admin' && (
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 설정 모달
// ============================================

function SettingsModal({ onClose }) {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await api.getSettings();
      setSettings(data);
    } catch (error) {
      toast.error('설정 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, field, value) => {
    setSettings(prev => prev.map(s =>
      s.setting_key === key ? { ...s, [field]: value } : s
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      toast.success('설정이 저장되었습니다.');
      onClose();
    } catch (error) {
      toast.error('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const togglePassword = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSettingLabel = (key) => {
    const labels = {
      openai_api_key: 'OpenAI API Key',
      anthropic_api_key: 'Anthropic API Key',
      use_openai: 'OpenAI 사용',
      use_anthropic: 'Anthropic 사용',
      default_model: '기본 AI 모델',
      max_tokens: '최대 토큰 수',
      temperature: 'AI 창의성 (Temperature)'
    };
    return labels[key] || key;
  };

  const modelOptions = [
    // 저렴한 모델 (추천)
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini', price: '$0.15/1M', quality: 3, badge: '추천', tier: 'budget' },
    { value: 'claude-3-haiku', label: 'Claude 3 Haiku', price: '$0.25/1M', quality: 3, badge: '최저가', tier: 'budget' },
    { value: 'claude-3.5-haiku', label: 'Claude 3.5 Haiku', price: '$1/1M', quality: 4, badge: '가성비', tier: 'budget' },
    // 중급 모델
    { value: 'gpt-4o', label: 'GPT-4o', price: '$5/1M', quality: 4, badge: '고품질', tier: 'standard' },
    { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet', price: '$3/1M', quality: 5, badge: '균형', tier: 'standard' },
    // 고급 모델
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', price: '$10/1M', quality: 4, badge: '', tier: 'premium' },
    { value: 'claude-3-opus', label: 'Claude 3 Opus', price: '$15/1M', quality: 5, badge: '최고품질', tier: 'premium' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Key className="text-purple-500" /> API 설정
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-purple-500" size={32} />
            </div>
          ) : (
            <div className="space-y-6">
              {/* API Keys Section */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Key size={18} className="text-slate-600" />
                  API 키 설정
                </h3>
                <div className="space-y-4">
                  {settings.filter(s => s.setting_type === 'password').map(setting => (
                    <div key={setting.setting_key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-700">
                          {getSettingLabel(setting.setting_key)}
                        </label>
                        <button
                          type="button"
                          onClick={() => handleChange(setting.setting_key, 'is_enabled', !setting.is_enabled)}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-all ${
                            setting.is_enabled
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-200 text-slate-500'
                          }`}
                        >
                          {setting.is_enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                          {setting.is_enabled ? '사용' : '미사용'}
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPasswords[setting.setting_key] ? 'text' : 'password'}
                          value={setting.setting_value || ''}
                          onChange={(e) => handleChange(setting.setting_key, 'setting_value', e.target.value)}
                          className="w-full px-4 py-2.5 pr-12 rounded-lg border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                          placeholder="API 키를 입력하세요"
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword(setting.setting_key)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[setting.setting_key] ? <EyeOff size={18} /> : <EyeIcon size={18} />}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">{setting.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Toggle Settings */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <ToggleRight size={18} className="text-slate-600" />
                  API 사용 설정
                </h3>
                <div className="space-y-3">
                  {settings.filter(s => s.setting_type === 'boolean').map(setting => (
                    <div key={setting.setting_key} className="flex items-center justify-between py-2">
                      <div>
                        <span className="font-medium text-slate-700">{getSettingLabel(setting.setting_key)}</span>
                        <p className="text-xs text-slate-500">{setting.description}</p>
                      </div>
                      <button
                        onClick={() => handleChange(setting.setting_key, 'setting_value', setting.setting_value === 'true' ? 'false' : 'true')}
                        className={`w-14 h-7 rounded-full transition-all relative ${
                          setting.setting_value === 'true' ? 'bg-purple-500' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                          setting.setting_value === 'true' ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Model Settings */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Settings size={18} className="text-slate-600" />
                  모델 설정
                </h3>
                <div className="space-y-4">
                  {settings.filter(s => s.setting_type === 'select').map(setting => (
                    <div key={setting.setting_key} className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {getSettingLabel(setting.setting_key)}
                      </label>
                      {/* 티어별 모델 선택 UI */}
                      {['budget', 'standard', 'premium'].map(tier => {
                        const tierModels = modelOptions.filter(m => m.tier === tier);
                        if (tierModels.length === 0) return null;
                        const tierLabels = { budget: '💰 저렴한 모델', standard: '⚡ 중급 모델', premium: '👑 프리미엄 모델' };
                        return (
                          <div key={tier} className="mb-2">
                            <p className="text-[11px] font-medium text-slate-400 mb-1.5">{tierLabels[tier]}</p>
                            <div className="grid grid-cols-2 gap-1.5">
                              {tierModels.map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={`p-2.5 rounded-lg border-2 transition-all text-left relative ${
                                    setting.setting_value === opt.value
                                      ? opt.value.includes('gpt')
                                        ? 'border-emerald-500 bg-emerald-50'
                                        : 'border-orange-500 bg-orange-50'
                                      : 'border-slate-200 bg-white hover:border-slate-300'
                                  }`}
                                  onClick={() => handleChange(setting.setting_key, 'setting_value', opt.value)}
                                >
                                  {opt.badge && (
                                    <span className={`absolute -top-1.5 -right-1.5 text-[9px] px-1 py-0.5 rounded-full font-bold ${
                                      opt.badge === '추천' || opt.badge === '가성비' ? 'bg-violet-500 text-white' :
                                      opt.badge === '최저가' ? 'bg-green-500 text-white' :
                                      opt.badge === '고품질' || opt.badge === '균형' ? 'bg-blue-500 text-white' :
                                      opt.badge === '최고품질' ? 'bg-amber-500 text-white' : 'bg-slate-500 text-white'
                                    }`}>
                                      {opt.badge}
                                    </span>
                                  )}
                                  <div className="flex items-start gap-1.5">
                                    <span className="text-sm">{opt.value.includes('gpt') ? '🤖' : '🧠'}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className={`font-medium text-xs ${
                                        setting.setting_value === opt.value
                                          ? opt.value.includes('gpt') ? 'text-emerald-700' : 'text-orange-700'
                                          : 'text-slate-700'
                                      }`}>
                                        {opt.label}
                                      </div>
                                      <div className="flex items-center gap-1.5 mt-0.5">
                                        <span className="text-[9px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-medium">
                                          {opt.price}
                                        </span>
                                        <span className="text-[9px] flex items-center">
                                          {[...Array(5)].map((_, i) => (
                                            <span key={i} className={i < opt.quality ? 'text-amber-400' : 'text-slate-200'}>★</span>
                                          ))}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-xs text-slate-500">{setting.description}</p>
                    </div>
                  ))}

                  {settings.filter(s => s.setting_type === 'number').map(setting => (
                    <div key={setting.setting_key} className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {getSettingLabel(setting.setting_key)}
                      </label>
                      <input
                        type="number"
                        step={setting.setting_key === 'temperature' ? '0.1' : '100'}
                        min={setting.setting_key === 'temperature' ? '0' : '100'}
                        max={setting.setting_key === 'temperature' ? '1' : '8000'}
                        value={setting.setting_value || ''}
                        onChange={(e) => handleChange(setting.setting_key, 'setting_value', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-purple-500"
                      />
                      <p className="text-xs text-slate-500">{setting.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 글 목록 아이템
// ============================================

function PostItem({ post, onSelect, onDelete }) {
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await api.deletePost(post.id);
      toast.success('삭제됨');
      onDelete();
    }
  };

  const statusClasses = {
    draft: 'bg-amber-100 text-amber-700',
    published: 'bg-emerald-100 text-emerald-700',
    archived: 'bg-slate-100 text-slate-600',
  };

  const statusLabels = {
    draft: '대기중',
    published: '발행됨',
    archived: '보관',
  };

  return (
    <div
      className="card p-5 cursor-pointer hover:-translate-y-1 transition-all duration-200 group"
      onClick={() => onSelect(post)}
    >
      <div className="flex gap-2 mb-3 flex-wrap">
        <span className={`badge ${post.site === 'finance' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
          {post.site === 'finance' ? '💰 금융' : '🔮 운세'}
        </span>
        <span className={`badge ${statusClasses[post.status]}`}>
          {statusLabels[post.status]}
        </span>
        {post.seo_grade && (
          <span className={`badge ${
            post.seo_grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
            post.seo_grade === 'B' ? 'bg-blue-100 text-blue-700' :
            post.seo_grade === 'C' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            SEO {post.seo_grade} ({post.seo_score}점)
          </span>
        )}
      </div>
      <h3 className="font-semibold text-slate-800 mb-3 line-clamp-2 leading-relaxed">{post.title}</h3>
      <div className="flex justify-between items-center text-sm">
        <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-md text-xs">{post.keyword}</span>
        <span className="text-slate-400">
          {new Date(post.created_at).toLocaleDateString('ko-KR')}
        </span>
      </div>
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <button
          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            post.is_favorite
              ? 'bg-amber-100 text-amber-500'
              : 'bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-500'
          }`}
          onClick={async (e) => {
            e.stopPropagation();
            await api.toggleFavorite(post.id);
            onDelete();
          }}
        >
          <Star size={16} fill={post.is_favorite ? 'currentColor' : 'none'} />
        </button>
        <button
          className="w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition-colors"
          onClick={handleDelete}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// 메인 App 컴포넌트
// ============================================

function App() {
  // 인증 상태
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [sites, setSites] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showTrendsModal, setShowTrendsModal] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [showExternalPublishModal, setShowExternalPublishModal] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ site: '', status: 'draft', search: '' });
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('darkMode') === 'true';
    }
    return false;
  });

  // 로그아웃
  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    toast.success('로그아웃되었습니다.');
  };

  // 다크모드 적용
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // 사이트 목록 로드
  useEffect(() => {
    api.getSites().then(res => setSites(res.data.sites || []));
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      let postsRes;
      if (filter.status === 'favorites') {
        postsRes = await api.getFavorites();
      } else if (filter.status === 'scheduled') {
        postsRes = await api.getScheduledPosts();
      } else {
        postsRes = await api.getPosts(filter);
      }
      const statsRes = await api.getStats();
      setPosts(postsRes.data.posts);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('데이터 로드 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filter]);

  const handleSelectPost = async (post) => {
    const res = await api.getPost(post.id);
    setSelectedPost(res.data);
    setShowDetailModal(true);
  };

  // 로그인 안 된 경우 로그인 페이지 표시
  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'font-sans',
          style: { borderRadius: '12px' }
        }}
      />

      {/* 헤더 */}
      <header className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white pb-20">
        <div className="max-w-7xl mx-auto px-6 pt-8 pb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <FileText size={28} />
                블로그 글 자동 생성기
              </h1>
              <p className="mt-2 text-white/70 text-sm">
                {sites.length > 0 ? sites.map(s => s.base_url).join(' · ') : '사이트 로딩중...'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* 사용자 정보 */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20">
                <User size={16} />
                <span className="text-sm font-medium">{user.name || user.username}</span>
                {user.role === 'admin' && (
                  <span className="px-1.5 py-0.5 bg-amber-400 text-amber-900 text-xs rounded font-bold">관리자</span>
                )}
              </div>

              <button
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 flex items-center justify-center transition-all"
                onClick={() => setDarkMode(!darkMode)}
                title={darkMode ? '라이트 모드' : '다크 모드'}
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              <button
                className="w-10 h-10 rounded-xl bg-red-500/80 hover:bg-red-500 backdrop-blur border border-white/20 flex items-center justify-center transition-all"
                onClick={handleLogout}
                title="로그아웃"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* 기능 메뉴 바 */}
        <div className="max-w-7xl mx-auto px-6 pt-2 pb-4">
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowDashboardModal(true)}
            >
              <LayoutDashboard size={16} />
              성과 대시보드
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowCostModal(true)}
            >
              <DollarSign size={16} />
              비용 추적
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowSchedulerModal(true)}
            >
              <Clock size={16} />
              자동화 스케줄러
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowCalendarModal(true)}
            >
              <Calendar size={16} />
              콘텐츠 캘린더
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowAnalyticsModal(true)}
            >
              <PieChart size={16} />
              키워드 분석
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowTrendsModal(true)}
            >
              <TrendingUp size={16} />
              트렌드 키워드
            </button>
            <button
              className="px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium flex items-center gap-2 transition-all"
              onClick={() => setShowKeywordModal(true)}
            >
              <Settings size={16} />
              키워드 관리
            </button>
            {user.role === 'admin' && (
              <>
                <button
                  className="px-3 py-2 rounded-lg bg-amber-500/80 hover:bg-amber-500 text-sm font-medium flex items-center gap-2 transition-all"
                  onClick={() => setShowUserManagementModal(true)}
                >
                  <Users size={16} />
                  사용자 관리
                </button>
                <button
                  className="px-3 py-2 rounded-lg bg-slate-600/80 hover:bg-slate-600 text-sm font-medium flex items-center gap-2 transition-all"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <Key size={16} />
                  API 설정
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-7xl mx-auto px-6 -mt-12">
        {/* 통계 카드 */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard title="전체 글" value={stats.total || 0} icon={FileText} color="blue" />
          <StatsCard title="대기중" value={stats.draft || 0} icon={RefreshCw} color="orange" />
          <StatsCard title="발행됨" value={stats.published || 0} icon={Check} color="green" />
          <StatsCard title="오늘 생성" value={stats.today || 0} icon={Plus} color="purple" />
        </section>

        {/* 액션 바 */}
        <section className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="제목 또는 키워드 검색..."
                value={filter.search}
                onChange={e => setFilter({ ...filter, search: e.target.value })}
                className="input pl-9 w-64"
              />
            </div>
            <select
              className="select"
              value={filter.site}
              onChange={e => setFilter({ ...filter, site: e.target.value })}
            >
              <option value="">전체 사이트</option>
              {sites.map(s => (
                <option key={s.id} value={s.site_key}>
                  {s.icon} {s.site_name}
                </option>
              ))}
            </select>
            <select
              className="select"
              value={filter.status}
              onChange={e => setFilter({ ...filter, status: e.target.value })}
            >
              <option value="draft">대기중</option>
              <option value="published">발행됨</option>
              <option value="archived">보관</option>
              <option value="favorites">⭐ 즐겨찾기</option>
              <option value="scheduled">📅 예약됨</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  const res = await api.exportPosts({ site: filter.site, status: filter.status });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'posts.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('CSV 다운로드 완료');
                } catch (error) {
                  toast.error('내보내기 실패');
                }
              }}
            >
              <Download size={16} />
              CSV
            </button>
            <button className="btn btn-secondary" onClick={loadData}>
              <RefreshCw size={16} />
              새로고침
            </button>
            <button
              className="btn btn-secondary"
              onClick={async () => {
                const site = filter.site || 'finance';
                try {
                  const res = await api.getUnusedKeywords(site, 5);
                  const keywords = res.data.keywords || [];
                  if (keywords.length === 0) {
                    toast.error('미사용 키워드가 없습니다');
                    return;
                  }
                  toast.success(`미사용 키워드 ${keywords.length}개 발견! 생성 모달에서 확인하세요.`);
                  setShowGenerateModal(true);
                } catch (error) {
                  toast.error('키워드 조회 실패');
                }
              }}
            >
              <Zap size={16} />
              자동 순환
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowGenerateModal(true)}
            >
              <Sparkles size={16} />
              글 생성하기
            </button>
          </div>
        </section>

        {/* 글 목록 */}
        <section className="min-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <RefreshCw size={40} className="animate-spin" />
              <p className="mt-4 text-lg">로딩 중...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <FileText size={56} />
              <p className="mt-4 text-lg">생성된 글이 없습니다.</p>
              <button
                className="btn btn-primary mt-6"
                onClick={() => setShowGenerateModal(true)}
              >
                <Sparkles size={16} />
                첫 글 생성하기
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map(post => (
                <PostItem
                  key={post.id}
                  post={post}
                  onSelect={handleSelectPost}
                  onDelete={loadData}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* 푸터 */}
      <footer className="mt-16 py-8 text-center text-sm text-slate-400">
        <p>Made with ❤️ for content creators</p>
      </footer>

      {/* 모달 */}
      <GenerateModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={loadData}
      />

      <PostDetailModal
        post={selectedPost}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onStatusChange={loadData}
        onThumbnailClick={() => {
          setShowDetailModal(false);
          setShowThumbnailModal(true);
        }}
        onExternalPublish={() => {
          setShowDetailModal(false);
          setShowExternalPublishModal(true);
        }}
      />

      <KeywordManageModal
        isOpen={showKeywordModal}
        onClose={() => setShowKeywordModal(false)}
      />

      <DashboardModal
        isOpen={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
      />

      <CostModal
        isOpen={showCostModal}
        onClose={() => setShowCostModal(false)}
      />

      <SchedulerModal
        isOpen={showSchedulerModal}
        onClose={() => setShowSchedulerModal(false)}
      />

      {showCalendarModal && (
        <ContentCalendar
          onClose={() => setShowCalendarModal(false)}
          onSelectPost={(post) => {
            setSelectedPost(post);
            setShowDetailModal(true);
            setShowCalendarModal(false);
          }}
        />
      )}

      {showAnalyticsModal && (
        <KeywordAnalytics onClose={() => setShowAnalyticsModal(false)} />
      )}

      {showTrendsModal && (
        <TrendKeywords
          onClose={() => setShowTrendsModal(false)}
          onGenerate={async (keyword, site) => {
            setShowTrendsModal(false);
            setShowGenerateModal(true);
            // 트렌드 키워드로 자동 생성 로직 (모달에서 처리)
          }}
        />
      )}

      {showThumbnailModal && selectedPost && (
        <ThumbnailGenerator
          post={selectedPost}
          onClose={() => setShowThumbnailModal(false)}
          onUpdate={loadData}
        />
      )}

      {showExternalPublishModal && selectedPost && (
        <ExternalPublisher
          post={selectedPost}
          onClose={() => setShowExternalPublishModal(false)}
        />
      )}

      {showUserManagementModal && (
        <UserManagement onClose={() => setShowUserManagementModal(false)} />
      )}

      {showSettingsModal && (
        <SettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
    </div>
  );
}

export default App;
